/**
 * 文章详情页面
 * 显示完整的文章内容和元数据
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Layout, Card, Typography, Tag, Space, Button, Spin, message,
  Divider, Modal, Form, Input, Select, Breadcrumb, Tooltip
} from 'antd'
import {
  ArrowLeftOutlined, EditOutlined, DeleteOutlined, EyeOutlined,
  ClockCircleOutlined, CalendarOutlined, UserOutlined, GlobalOutlined,
  TagOutlined, FolderOutlined, StarOutlined, ShareAltOutlined,
  ExclamationCircleOutlined, ToolOutlined
} from '@ant-design/icons'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { api } from '../services/api'
import dayjs from 'dayjs'
import './ArticleDetail.css'

const { Header, Content } = Layout
const { Title, Text, Paragraph } = Typography
const { confirm } = Modal

// Markdown渲染组件配置
const MarkdownComponents = {
  code({ node, inline, className, children, ...props }: any) {
    const match = /language-(\w+)/.exec(className || '')
    return !inline && match ? (
      <SyntaxHighlighter
        style={tomorrow}
        language={match[1]}
        PreTag="div"
        {...props}
      >
        {String(children).replace(/\n$/, '')}
      </SyntaxHighlighter>
    ) : (
      <code className={className} {...props}>
        {children}
      </code>
    )
  },
  img({ src, alt, ...props }: any) {
    return (
      <img
        src={src}
        alt={alt}
        style={{ maxWidth: '100%', height: 'auto', display: 'block', margin: '16px auto' }}
        {...props}
      />
    )
  }
}

interface Article {
  id: number
  title: string
  url: string
  content: string
  summary: string
  markdown_content: string
  site_name: string
  author: string | null
  published_at: string | null
  collected_at: string
  reading_time: number
  word_count: number
  cover_image: string | null
  favicon: string | null
  is_read: boolean
  is_featured: boolean
  last_read_at: string | null
  tags: Array<{ id: number; name: string; color: string }>
  folder: { id: number; name: string } | null
}

const ArticleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  
  const [article, setArticle] = useState<Article | null>(null)
  const [loading, setLoading] = useState(true)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [toolAnalyzing, setToolAnalyzing] = useState(false)
  const [form] = Form.useForm()
  
  const [availableTags, setAvailableTags] = useState<Array<{ id: number; name: string; color: string }>>([])
  const [availableFolders, setAvailableFolders] = useState<Array<{ id: number; name: string }>>([])

  useEffect(() => {
    if (id) {
      loadArticle()
      loadTagsAndFolders()
    }
  }, [id])

  const loadArticle = async () => {
    try {
      setLoading(true)
      const response = await api.articles.get(Number(id))

      if (response.success) {
        setArticle(response.data)

        // 设置表单初始值
        form.setFieldsValue({
          title: response.data.title,
          tags: response.data.tags.map((t: any) => t.id),
          folder_id: response.data.folder?.id
        })

        // 总是标记为已读并更新 last_read_at
        markAsRead()
      }
    } catch (error: any) {
      console.error('加载文章失败:', error)
      message.error('加载文章失败')
    } finally {
      setLoading(false)
    }
  }

  const markAsRead = async () => {
    try {
      await api.articles.batchSetRead([Number(id)])
      // 静默更新文章状态
      if (article) {
        setArticle({ ...article, is_read: true, last_read_at: new Date().toISOString() })
      }
    } catch (error) {
      console.error('标记为已读失败:', error)
      // 不显示错误消息，因为这是后台操作
    }
  }

  const handleToggleFeatured = async () => {
    if (!article) return

    try {
      const newFeaturedStatus = !article.is_featured
      await api.articles.update(Number(id), {
        is_featured: newFeaturedStatus
      })

      // 更新前端状态
      setArticle({ ...article, is_featured: newFeaturedStatus })
      message.success(newFeaturedStatus ? '已设为精选' : '已取消精选')
    } catch (error) {
      console.error('设置精选失败:', error)
      message.error('操作失败')
    }
  }

  const loadTagsAndFolders = async () => {
    try {
      const [tagsRes, foldersRes] = await Promise.all([
        api.tags.list({ page_size: 100 }),
        api.folders.list({ page_size: 100 })
      ])
      
      if (tagsRes.success) {
        setAvailableTags(tagsRes.data || [])
      }

      if (foldersRes.success) {
        setAvailableFolders(foldersRes.data || [])
      }
    } catch (error) {
      console.error('加载标签和文件夹失败:', error)
    }
  }

  const handleEdit = async (values: any) => {
    try {
      // 将标签ID转换为标签名称
      const selectedTagNames = values.tags ?
        values.tags.map((tagId: number) => {
          const tag = availableTags.find(t => t.id === tagId)
          return tag ? tag.name : null
        }).filter(Boolean) : []

      const response = await api.articles.update(Number(id), {
        title: values.title,
        tags: selectedTagNames,  // 使用标签名称列表，字段名为 tags
        folder_id: values.folder_id
      })
      
      if (response.success) {
        message.success('更新成功')
        setEditModalVisible(false)
        loadArticle()
      }
    } catch (error: any) {
      console.error('更新文章失败:', error)
      message.error('更新失败')
    }
  }

  const handleDelete = () => {
    confirm({
      title: '确认删除',
      icon: <ExclamationCircleOutlined />,
      content: '确定要删除这篇文章吗？此操作不可恢复。',
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await api.articles.delete(Number(id))
          
          if (response.success) {
            message.success('删除成功')
            navigate('/search')
          }
        } catch (error: any) {
          console.error('删除文章失败:', error)
          message.error('删除失败')
        }
      }
    })
  }

  const handleShare = () => {
    const url = article?.url || window.location.href
    navigator.clipboard.writeText(url)
    message.success('原文链接已复制到剪贴板')
  }

  const handleMarkAsTool = async () => {
    if (!article) return
    try {
      setToolAnalyzing(true)
      const response: any = await api.tools.fromArticle(article.id)
      if (response.success) {
        message.success('已分析并记入工具库')
        navigate(`/tools?q=${encodeURIComponent(response.data.name)}`)
      }
    } catch (error) {
      console.error('文章转工具失败:', error)
      message.error('文章转工具失败')
    } finally {
      setToolAnalyzing(false)
    }
  }

  // 预处理Markdown内容，修复格式问题
  const preprocessMarkdown = (markdown: string) => {
    // 修复图片和标题之间缺少换行符的问题
    // 在图片后面如果直接跟着标题，则添加两个换行符
    return markdown.replace(/(\!\[.*?\]\(.*?\))(\s*)(#)/g, '$1\n\n$3')
  }

  // 渲染Markdown内容的组件
  const renderMarkdown = (markdown: string) => {
    const processedMarkdown = preprocessMarkdown(markdown)
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={MarkdownComponents}
      >
        {processedMarkdown}
      </ReactMarkdown>
    )
  }

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" tip="加载中..." />
      </div>
    )
  }

  if (!article) {
    return (
      <div className="error-container">
        <Text>文章不存在</Text>
        <Button onClick={() => navigate('/search')}>返回搜索</Button>
      </div>
    )
  }

  return (
    <Layout className="article-detail-layout">
      <Header className="article-header">
        <div className="header-left">
          <Button 
            icon={<ArrowLeftOutlined />}
            onClick={() => {
              // 优先尝试从搜索页返回，其次文章列表页
              const searchState = sessionStorage.getItem('search_list_state')
              const savedState = sessionStorage.getItem('articles_list_state')
              let backUrl = '/articles'

              if (searchState) {
                try {
                  const s = JSON.parse(searchState)
                  const params = new URLSearchParams()
                  if (s.query) params.set('q', s.query)
                  if (s.page && s.page > 1) params.set('page', String(s.page))
                  if (s.search_type && s.search_type !== 'keyword') params.set('type', s.search_type)
                  if (s.tags && s.tags.length > 0) params.set('tags', s.tags.join(','))
                  if (s.folder_id) params.set('folder_id', String(s.folder_id))
                  if (s.sort_by && s.sort_by !== 'collected_at') params.set('sort', s.sort_by)
                  if (s.order && s.order !== 'desc') params.set('order', s.order)
                  const qs = params.toString()
                  backUrl = qs ? `/search?${qs}` : '/search'
                } catch (e) {
                  // 解析失败，使用默认路径
                }
              } else if (savedState) {
                try {
                  const state = JSON.parse(savedState)
                  const params = new URLSearchParams()
                  if (state.current && state.current > 1) {
                    params.set('page', state.current)
                  }
                  if (state.filters) {
                    if (state.filters.is_archived) {
                      params.set('archived', '1')
                    }
                    if (state.filters.is_read === true) {
                      params.set('read', '1')
                    } else if (state.filters.is_read === false) {
                      params.set('unread', '1')
                    }
                    if (state.filters.is_featured === true) {
                      params.set('featured', '1')
                    }
                    if (state.filters.tag_id) {
                      params.set('tag_id', state.filters.tag_id)
                    }
                    if (state.filters.folder_id) {
                      params.set('folder_id', state.filters.folder_id)
                    }
                  }
                  const queryString = params.toString()
                  if (queryString) {
                    backUrl = `/articles?${queryString}`
                  }
                } catch (e) {
                  // 解析失败，使用默认路径
                }
              }

              navigate(backUrl)
            }}
            type="text"
            style={{ color: 'white' }}
          >
            返回列表
          </Button>
          <Breadcrumb
            items={[
              { title: '首页', onClick: () => navigate('/') },
              { title: '搜索', onClick: () => navigate('/search') },
              { 
                title: (
                  <span 
                    style={{ 
                      color: 'var(--muted)',
                      maxWidth: 300,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'inline-block',
                      verticalAlign: 'middle'
                    }}
                    title={article.title}
                  >
                    {article.title.length > 30 ? article.title.substring(0, 30) + '...' : article.title}
                  </span>
                )
              }
            ]}
          />
        </div>
        
        <div className="header-right">
          <Space>
            <Tooltip title="分析文章内容并记入工具库">
              <Button
                icon={<ToolOutlined />}
                onClick={handleMarkAsTool}
                loading={toolAnalyzing}
              >
                记为工具
              </Button>
            </Tooltip>
            <Tooltip title={article.is_featured ? "取消精选" : "设为精选"}>
              <Button
                icon={<StarOutlined />}
                onClick={handleToggleFeatured}
                type={article.is_featured ? "primary" : "default"}
                style={article.is_featured ? { color: '#faad14' } : {}}
              >
                {article.is_featured ? "已精选" : "设为精选"}
              </Button>
            </Tooltip>
            <Tooltip title="查看原文">
              <Button
                icon={<EyeOutlined />}
                onClick={() => window.open(article.url, '_blank')}
              >
                原文
              </Button>
            </Tooltip>
            <Tooltip title="分享">
              <Button
                icon={<ShareAltOutlined />}
                onClick={handleShare}
              >
                分享
              </Button>
            </Tooltip>
            <Tooltip title="编辑">
              <Button
                icon={<EditOutlined />}
                onClick={() => setEditModalVisible(true)}
              >
                编辑
              </Button>
            </Tooltip>
            <Tooltip title="删除">
              <Button
                icon={<DeleteOutlined />}
                onClick={handleDelete}
                danger
              >
                删除
              </Button>
            </Tooltip>
          </Space>
        </div>
      </Header>

      <Content className="article-content">
        <Card className="article-card">
          {/* 文章头部 */}
          <div className="article-title-section">
            <Title level={1} className="article-main-title">
              {article.title}
            </Title>
            
            <div className="article-metadata">
              <Space size="middle" wrap>
                {article.site_name && (
                  <Space size="small">
                    <GlobalOutlined />
                    <Text>{article.site_name}</Text>
                  </Space>
                )}
                
                {article.author && (
                  <Space size="small">
                    <UserOutlined />
                    <Text>{article.author}</Text>
                  </Space>
                )}
                
                <Space size="small">
                  <ClockCircleOutlined />
                  <Text>{article.reading_time} 分钟阅读</Text>
                </Space>
                
                <Space size="small">
                  <CalendarOutlined />
                  <Text>
                    收藏于 {dayjs(article.collected_at).format('YYYY-MM-DD HH:mm')}
                  </Text>
                </Space>
              </Space>
            </div>

            {/* 标签和文件夹 */}
            <div className="article-taxonomy">
              {article.tags && article.tags.length > 0 && (
                <div className="taxonomy-section">
                  <TagOutlined style={{ marginRight: 8 }} />
                  <Space size="small" wrap>
                    {article.tags.map(tag => (
                      <Tag 
                        key={tag.id} 
                        color={tag.color || 'blue'}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/search?tag=${tag.name}`)}
                      >
                        {tag.name}
                      </Tag>
                    ))}
                  </Space>
                </div>
              )}
              
              {article.folder && (
                <div className="taxonomy-section">
                  <FolderOutlined style={{ marginRight: 8 }} />
                  <Space size="small" wrap>
                    <Tag
                      key={article.folder.id}
                      icon={<FolderOutlined />}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/search?folder=${article.folder.id}`)}
                    >
                      {article.folder.name}
                    </Tag>
                  </Space>
                </div>
              )}
            </div>
          </div>

          <Divider />

          {/* 文章内容 */}
          <div className="markdown-content">
            {renderMarkdown(article.markdown_content || article.content)}
          </div>

          <Divider />

          {/* 文章底部信息 */}
          <div className="article-footer">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text type="secondary">
                原文链接: <a href={article.url} target="_blank" rel="noopener noreferrer">{article.url}</a>
              </Text>
              <Text type="secondary">
                字数统计: {article.word_count} 字
              </Text>
            </Space>
          </div>
        </Card>

        {/* 相似文章推荐区域（预留） */}
        <Card 
          title="相似文章推荐" 
          className="similar-articles-card"
          style={{ marginTop: 24 }}
        >
          <Text type="secondary">AI推荐功能即将上线...</Text>
        </Card>
      </Content>

      {/* 编辑模态框 */}
      <Modal
        title="编辑文章"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleEdit}
        >
          <Form.Item
            name="title"
            label="标题"
            rules={[{ required: true, message: '请输入标题' }]}
          >
            <Input placeholder="文章标题" />
          </Form.Item>

          <Form.Item
            name="tags"
            label="标签"
          >
            <Select
              mode="multiple"
              placeholder="选择标签"
              maxTagCount={5}
            >
              {availableTags.map(tag => (
                <Select.Option key={tag.id} value={tag.id}>
                  <Tag color={tag.color}>{tag.name}</Tag>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="folder_id"
            label="文件夹"
          >
            <Select
              placeholder="选择文件夹"
              allowClear
            >
              {availableFolders.map(folder => (
                <Select.Option key={folder.id} value={folder.id}>
                  {folder.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  )
}

export default ArticleDetail
